'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, Film, ArrowLeft, Check, Eye, EyeOff } from 'lucide-react';
import Image from 'next/image';
import { AnimatedContentBackground } from '@/components/AnimatedContentBackground';
import { useSeasonalLogo } from '@/hooks/useSeasonalLogo';
import { getAuthRedirect, authErrorMessage, authRedirectQuery } from '@/lib/utils';
import { setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '@/lib/firebase';

export default function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Firebase keeps sessions by default, so "remember me" starts checked;
  // unchecking it limits the session to this browser tab/window
  const [rememberMe, setRememberMe] = useState(true);
  const [redirectQuery, setRedirectQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { user, loading: authLoading, signIn, signInWithGoogle } = useAuth();
  const router = useRouter();
  const { seasonalLogo, themeColor, loading: logoLoading } = useSeasonalLogo();

  // Read ?redirect= after mount (window isn't available during SSR)
  useEffect(() => {
    setRedirectQuery(authRedirectQuery());
  }, []);

  // Already signed in (e.g. opened a stale sign-in link) — go on to the destination
  useEffect(() => {
    if (!authLoading && user && !loading) {
      router.replace(getAuthRedirect());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence).catch(() => {});
      await signIn(email.trim(), password);
      toast.success('Signed in successfully!');
      router.push(getAuthRedirect());
    } catch (error: any) {
      toast.error(authErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      toast.success('Signed in with Google!');
      router.push(getAuthRedirect());
    } catch (error: any) {
      // Don't show error if user simply closed the popup
      // ACCOUNT_EXISTS_LINK_REQUIRED already showed its own explanatory toast
      if (error?.code !== 'auth/popup-closed-by-user' && error?.code !== 'auth/cancelled-popup-request' && error?.message !== 'ACCOUNT_EXISTS_LINK_REQUIRED') {
        toast.error('Failed to sign in with Google');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Animated Content Background */}
      <AnimatedContentBackground />

      {/* Content */}
      <div className="relative z-30 flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-md">
          {/* Back to Home Button */}
          <div className="mb-6">
            <Link
              href="/"
              className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Epify
            </Link>
          </div>

          {/* Logo/Brand Section */}
          <div className="text-center mb-8">
            <div className="mb-4 h-[80px] flex items-center justify-center">
              {!logoLoading && (
                <Image
                  src={seasonalLogo?.url || "/image/epify.png"}
                  alt="Epify"
                  width={150}
                  height={150}
                  className="object-contain mx-auto"
                  style={{ maxHeight: '70px', width: 'auto', height: 'auto' }}
                  quality={100}
                  priority
                />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Welcome back!</h1>
            <p className="text-gray-400 text-sm">
              Sign in to track your favorite shows and movies
            </p>
          </div>

          {/* Sign In Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
            <form onSubmit={handleSignIn} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative group theme-input-group">
                  <style>{`
                    .theme-input-group:focus-within .theme-icon {
                      color: ${themeColor} !important;
                    }
                    .theme-input:focus {
                      border-color: ${themeColor}80 !important;
                      box-shadow: 0 0 0 2px ${themeColor}1A !important;
                    }
                  `}</style>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400 transition-colors theme-icon" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="theme-input h-12 pl-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300 text-sm font-medium">
                  Password
                </Label>
                <div className="relative group theme-input-group">
                  <style>{`
                    .theme-eye-icon:hover {
                      color: ${themeColor} !important;
                    }
                  `}</style>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 transition-colors theme-icon" />
                  </div>
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="theme-input h-12 pl-10 pr-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl transition-all"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center theme-eye-icon text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <style>{`
                  .theme-link:hover {
                    color: ${themeColor} !important;
                  }
                `}</style>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setRememberMe(!rememberMe)}
                    role="checkbox"
                    aria-checked={rememberMe}
                    aria-label="Remember me"
                    className="flex-shrink-0 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                    style={{
                      backgroundColor: 'rgb(31 41 55 / 0.5)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: rememberMe ? themeColor : 'rgb(55 65 81 / 0.5)',
                      width: '20px',
                      height: '20px',
                      minWidth: '20px',
                      minHeight: '20px',
                      maxWidth: '20px',
                      maxHeight: '20px'
                    }}
                  >
                    {rememberMe && <Check className="w-3.5 h-3.5 stroke-[3]" style={{ color: themeColor }} />}
                  </button>
                  <label
                    onClick={() => setRememberMe(!rememberMe)}
                    className="theme-link ml-2 text-sm text-gray-400 cursor-pointer select-none font-medium transition-colors"
                  >
                    Remember me
                  </label>
                </div>
                <Link
                  href="/auth/forgot-password"
                  className="theme-link text-sm text-gray-400 transition-colors font-medium"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-red-500/25"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-900/80 px-4 text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-12 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl border border-gray-300 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </button>

            {/* Sign Up Link */}
            <div className="text-center mt-6 pt-6 border-t border-gray-800/50">
              <p className="text-sm text-gray-400">
                Don't have an account?{' '}
                <Link
                  href={`/auth/signup${redirectQuery}`}
                  className="text-red-400 hover:text-red-300 font-medium transition-colors"
                >
                  Sign up →
                </Link>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-gray-500">
              Join thousands of users tracking their favorite content
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}